import React, { useEffect } from 'react'
import papa from 'papaparse'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { format } from 'date-fns'
import {
  getFormattedDateAndTime,
  SortDirectionEnum,
  TestResult,
  useStateWithDebounce,
  TestType,
  useTestsResultsAnalytics,
  Lab,
  TestsResultsAnalytics_testsResultsAnalytics_tests,
  TestSessionPaymentStatus,
  TestsResultsAnalytics as TestsResultsAnalyticsCsv,
  TestsResultsAnalyticsVariables as TestsResultsAnalyticsVariablesCsv,
  TypeFilter,
  UserPermissions,
  useAuth,
  SortByType,
} from '@modmd/data'
import { Downloading } from 'components/Downloading'
import { isInfluenza, useSortChangeHandler } from 'utils/helpers'
import { ROUTES } from 'internal-portal/constants/routes'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { AnalyticsTestsResultsDatalist } from 'sharedComponents/AnalyticsTestsResultsDatalist'
import { Box, Container } from 'components/Layout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import TextInput from 'components/TextInput'
import Icon from 'components/Icon'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import { CompaniesDropdown } from 'sharedComponents/CompaniesDropdown'
import { GroupsDropdown } from 'sharedComponents/GroupsDropdown'
import { COLOR, DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import { RESULTS_PER_PAGE, PAYMENT_METHOD_OPTIONS } from 'internal-portal/constants/filters'
import { LAB_OPTIONS, TYPE_FILTER_OPTIONS, TYPE_OPTIONS } from '../TestSessions/utils'
import { TESTS_RESULTS_ANALYTICS } from './operations'

type AnalyticsTestsResultsProps = RouteComponentProps

type FilterState = {
  location?: string
  page?: string
  direction?: SortDirectionEnum
  sortBy?: string
  dateFrom?: string
  dateTo?: string
  memberLike?: string
  companyOrGroupLike?: string
  user?: string
  result?: TestResult | 'pending'
  companyId?: string
  groupId?: string
  barcode?: string
  lab?: Lab | null
  type?: TestType
  resultsPerPage?: string
  paymentStatus?: TestSessionPaymentStatus
  paymentMethod?: string
  typeFilter?: TypeFilter
  insuranceCompany?: string
}

const TestsResultsAnalytics: React.VFC<AnalyticsTestsResultsProps> = () => {
  const { hasPermission } = useAuth()
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const history = useHistory()
  const searchState = useSearchState<FilterState>()
  const [isCollapsed, setCollapse] = React.useState(false)
  const { sorter, handleSortChange } = useSortChangeHandler()
  const [downloadableDataUrl, setDownloadableDataUrl] = React.useState<string>()
  const today = new Date()
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
  const [download, setDownload] = React.useState(false)
  const [getAnalyticsData, testsQuery] = useTestsResultsAnalytics()
  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const pageLength = Number(searchState.data.resultsPerPage) || Number(RESULTS_PER_PAGE[0].value)
  const page = Number(searchState.data.page) || 1

  const getData = () => {
    getAnalyticsData({
      variables: {
        filterData: {
          testedBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
          companyIds: searchState.data.companyId ? [searchState.data.companyId] : undefined,
          groupIds: searchState.data.groupId ? [searchState.data.groupId] : undefined,
          lab: searchState.data.lab,
          hasResult: searchState.data.result !== 'pending',
          result: Object.values(TestResult).some((result) => result === searchState.data.result)
            ? (searchState.data.result as TestResult)
            : undefined,
          location: searchState.data.location || undefined,
          type: searchState.data.type,
          barcode: searchState.data.barcode,
          user: searchState.data.memberLike
            ? { emailOrNameLike: searchState.data.memberLike, isParticipant: true }
            : null,
          paymentStatus: searchState.data.paymentStatus || undefined,
          paymentMethod: searchState.data.paymentMethod || undefined,
          typeFilter: searchState.data.typeFilter || undefined,
          insuranceCompany: searchState.data.insuranceCompany || undefined,
        },
        pagination: {
          from: pageLength * (page - 1),
          length: pageLength,
          sortBy: sorter?.sortBy || SortByType.RESULT_RECIEVED_AT,
          direction: sorter?.direction || SortDirectionEnum.ASC,
        },
      },
    })
  }

  useEffect(() => {
    void getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  const [getTestsCsv, { data: testsCsv, loading: testsCsvLoading }] = useLazyQuery<
    TestsResultsAnalyticsCsv,
    TestsResultsAnalyticsVariablesCsv
  >(TESTS_RESULTS_ANALYTICS)

  const testTypeOptions = {
    Covid19: 'Covid19 RT-PCR',
    SofiaInfluenza: 'Influenza Antigen',
    SofiaStrep: 'Strep Antigen',
    Lucira: 'Lucira',
    SofiaRsv: 'RSV Antigen',
    BioSignFlu: 'BioSign® Flu A+B Test',
  }

  React.useEffect(() => {
    if (download) {
      const testResult = testsCsv?.testsResultsAnalytics.tests.map(
        (test: TestsResultsAnalytics_testsResultsAnalytics_tests) => {
          const {
            barcode,
            eventId,
            result,
            secondResult,
            resultProvisionedAt,
            resultVerifiedAt,
            specimenType,
            TestSession,
            User,
            testedAt,
            eventpaymentMethod,
            Insurance,
            Company,
            Group,
          } = test
          const patient = `${User.firstName || ''} ${User.lastName || ''}`
          const birthDate = getFormattedDateAndTime(User.birthDate)
          const dateAppointment = getFormattedDateAndTime(TestSession.date, true)
          const testedAtDate = getFormattedDateAndTime(testedAt, true)
          const resultDate = getFormattedDateAndTime(resultProvisionedAt, true)
          const resultVerifiedDate = getFormattedDateAndTime(resultVerifiedAt, true)
          const paymentStatus = TestSession.paymentStatus || 'Invoicing'
          const paymentMethod = TestSession.Company?.id
            ? TestSession.paymentStatus === TestSessionPaymentStatus.Insurance
              ? 'Insurance'
              : 'Invoice'
            : 'Credit card'
          const zipCode = TestSession.zip || ''
          const resultFull = isInfluenza(TestSession.type as TestType)
            ? `A: ${result ?? ''} | B: ${(secondResult as string) ?? ''}`
            : result

          const testResult = {
            // CAMPOS REQUERIDOS (em ordem e com nomes exigidos):
            'Patient last name': User?.lastName || '',
            'Patient first name': User?.firstName || '',
            'Patient street address 1': User?.Address?.street || '',
            'Patient street address 2': User?.Address?.complement || '',
            'Patient city': User?.Address?.city || '',
            'Patient state': User?.Address?.state || '',
            'Patient phone number': User?.phoneNumber || '',
            'Patient date of birth': birthDate,
            'Patient race': User?.race || '',
            'Patient ethnicity': User?.ethnicity || '',
            'Patient gender': User?.gender || '',
            'Patient email': User.email,
            'Sample barcode': barcode,
            'Type of test':
              testTypeOptions[TestSession.type as keyof typeof testTypeOptions] ||
              TestSession.customTestType?.name,
            'Test result': resultFull,
            'Specimen collection date/time': testedAtDate,
            'Specimen received date/time': resultDate,
            'Test result date/time': dateAppointment,
            'Test result released date/time': resultVerifiedDate,
            'Specimen type': specimenType || '',
            // CAMPOS ADICIONAIS (não requeridos, mantidos com nomes originais):
            sessionId: TestSession.id,
            eventId,
            date: format(new Date(TestSession.date), 'dd/MM/yyyy'),
            time: format(new Date(TestSession.date), 'HH:mm'),
            'Company name': Company?.name || '',
            'Group Name': Group?.name || '',
            Status: TestSession.finishedAt ? 'Finished' : 'Pending',
            lab: TestSession.lab,
            zipCode,
            paymentStatus,
            'Invoice number': TestSession.invoiceId || '',
            'Invoice status': TestSession.invoiceDocNumber || '',
            'Insurance name': Insurance?.insuranceCompany || '',
            'Insurance id': Insurance?.id || '',
            patient,
            paymentMethod: eventpaymentMethod || paymentMethod,
          }

          return testResult
        }
      )
      if (testResult && !downloadableDataUrl) {
        const csv = papa.unparse(testResult, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `Tests-Results-${Date()}.csv`)
        document.body.appendChild(link)
        link.click()
        setDownload(false)
      } else if (downloadableDataUrl) {
        window.URL.revokeObjectURL(downloadableDataUrl)
        setDownloadableDataUrl('')
        setDownload(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testsCsv])

  const handleDownloadCsv = () => {
    getTestsCsv({
      variables: {
        pagination: {
          from: 0,
          length:
            testsQuery.data?.testsResultsAnalytics.pagination.total ||
            Number(RESULTS_PER_PAGE[0].value),
          sortBy: 'id',
        },
        filterData: {
          testedBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
          groupIds: searchState.data.groupId ? [searchState.data.groupId] : undefined,
          companyIds: searchState.data.companyId ? [searchState.data.companyId] : undefined,
          lab: searchState.data.lab,
          hasResult: searchState.data.result !== 'pending',
          result: Object.values(TestResult).some((result) => result === searchState.data.result)
            ? (searchState.data.result as TestResult)
            : undefined,
          location: searchState.data.location || undefined,
          type: searchState.data.type,
          barcode: searchState.data.barcode,
          paymentStatus: searchState.data.paymentStatus,
          paymentMethod: searchState.data.paymentMethod,
          typeFilter: searchState.data.typeFilter || undefined,
          insuranceCompany: searchState.data.insuranceCompany || undefined,
        },
      },
    })
    setDownload(true)
  }

  return (
    <DashboardPage
      Header={
        hasPermission([UserPermissions.DOWNLOAD_TEST_RESULTS_ANALYTICS_CSV]) && (
          <Box display="flex" justifyContent="flex-end" p="1rem">
            <Button appearance="filled" onClick={handleDownloadCsv} disabled={testsCsvLoading}>
              {testsCsvLoading ? <Downloading /> : 'Download csv'}
            </Button>
          </Box>
        )
      }
    >
      <Container>
        <AnalyticsTestsResultsDatalist
          tests={testsQuery.data?.testsResultsAnalytics.tests || []}
          pagination={testsQuery.data?.testsResultsAnalytics.pagination}
          isLoading={testsQuery.loading}
          tableHeader={
            <Box
              display="grid"
              gridGap="1rem"
              gridTemplateColumns={
                isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
              }
            >
              <DateInput
                id="dateFrom"
                name="dateFrom"
                label="Date From"
                value={filterData.dateFrom ? new Date(filterData.dateFrom) : lastWeek}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateFrom: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <DateInput
                id="dateTo"
                name="dateTo"
                label="Date To"
                value={filterData.dateTo ? new Date(filterData.dateTo) : today}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateTo: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <CompaniesDropdown
                value={searchState.data.companyId}
                onChange={(id) => {
                  setFilterData({ ...filterData, companyId: String(id), groupId: '' })
                }}
                hasBottomContent
              />
              <GroupsDropdown
                value={searchState.data.groupId}
                onChange={(id) => {
                  setFilterData({ ...filterData, groupId: String(id) })
                }}
                companyId={searchState.data.companyId}
                hasBottomContent
              />
              <SelectInput
                name="lab"
                label="Lab"
                value={filterData.lab || ''}
                onChange={handleInputChange}
                options={LAB_OPTIONS}
                hasBottomContent
              />
              <SelectInput
                name="typeFilter"
                label="Testing Type"
                value={filterData.typeFilter || ''}
                onChange={handleInputChange}
                options={TYPE_FILTER_OPTIONS}
                hasBottomContent
              />
              {isCollapsed && (
                <>
                  <SelectInput
                    name="result"
                    label="Result"
                    value={filterData.result || ''}
                    onChange={handleInputChange}
                    options={[
                      { value: '', label: 'All' },
                      ...Object.values(TestResult).map((result) => ({
                        value: result,
                        label: result,
                      })),
                      { value: 'pending', label: 'Pending' },
                    ]}
                    hasBottomContent
                  />
                  <TextInput
                    name="location"
                    label="Location"
                    value={filterData.location || ''}
                    onChange={handleInputChange}
                    leftIcon={<Icon.Search />}
                    placeholder="Search by Location"
                    hasBottomContent
                  />
                  <SelectInput
                    name="type"
                    label="Type"
                    value={filterData.type || ''}
                    onChange={handleInputChange}
                    options={TYPE_OPTIONS}
                    hasBottomContent
                  />
                  <TextInput
                    name="barcode"
                    label="Barcode"
                    value={filterData.barcode || ''}
                    onChange={handleInputChange}
                    leftIcon={<Icon.Search />}
                    placeholder="Search by Barcode"
                    hasBottomContent
                  />
                  <TextInput
                    name="memberLike"
                    label="Member"
                    value={filterData.memberLike || ''}
                    onChange={handleInputChange}
                    leftIcon={<Icon.Search />}
                    placeholder="Search by member name"
                    hasBottomContent
                  />
                  <SelectInput
                    name="paymentStatus"
                    label="Payment status"
                    value={filterData.paymentStatus || ''}
                    onChange={handleInputChange}
                    options={[
                      { value: '', label: 'All' },
                      ...Object.values(TestSessionPaymentStatus).map((status) => ({
                        value: status,
                        label: status,
                      })),
                    ]}
                    hasBottomContent
                  />
                  <SelectInput
                    name="paymentMethod"
                    label="Payment Method"
                    value={filterData.paymentMethod || ''}
                    onChange={handleInputChange}
                    options={PAYMENT_METHOD_OPTIONS}
                    hasBottomContent
                  />
                  <SelectInput
                    name="resultsPerPage"
                    label="Results per page"
                    value={filterData.resultsPerPage || ''}
                    onChange={handleInputChange}
                    options={RESULTS_PER_PAGE}
                    hasBottomContent
                  />
                  <TextInput
                    name="insuranceCompany"
                    label="Insurance Company"
                    value={filterData.insuranceCompany || ''}
                    onChange={handleInputChange}
                    leftIcon={<Icon.Search />}
                    placeholder="Search by insurance company"
                    hasBottomContent
                  />
                </>
              )}
              <Button onClick={() => setCollapse(!isCollapsed)} appearance="link" size="medium">
                {!isCollapsed ? (
                  <>
                    <Icon.ChevronDown color={COLOR.text} />
                    Show more filters
                  </>
                ) : (
                  <>
                    <Icon.ChevronUp color={COLOR.text} />
                    Hide filters
                  </>
                )}
              </Button>
              <Button onClick={getData} size="large">
                Filter
              </Button>
            </Box>
          }
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onRowClick={({ id, eventId }) =>
            eventId
              ? history.push(`/${ROUTES.EVENTS}/${eventId}`)
              : history.push(`/${ROUTES.TESTS}/${id}`)
          }
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={testsQuery.data?.testsResultsAnalytics.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default TestsResultsAnalytics
